import { ArrowDown, ArrowUp, Minus } from "@phosphor-icons/react";
import { forwardRef } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { Card } from "@/components/primitives/Card";
import { Skeleton } from "@/components/ui/feedback/Skeleton";
import type { MetricCardProps, TrendDirection } from "./MetricCard.types";

/* Exact compiled output of the pre-migration Tailwind utilities. Note:
 * tailwind-merge drops the value's `leading-none` because the later
 * text-xl/text-2xl class carries a font-size (fontSize conflicts with
 * leading), so line-height comes from the text class. Verified against the
 * frozen baseline. */

const trendToneStyles: Record<TrendDirection, CSSObject> = {
  up: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-success-soft) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-success-contrast) / var(--tw-text-opacity, 1))"
  },
  down: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-danger-soft) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-danger-contrast) / var(--tw-text-opacity, 1))"
  },
  neutral: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
  }
};

const trendIcon = {
  up: <ArrowUp size={11} weight="bold" />,
  down: <ArrowDown size={11} weight="bold" />,
  neutral: <Minus size={11} weight="bold" />
};

const MetricCardRoot = styled(Card, {
  label: "AgenticMetricCard-root"
})({
  minWidth: "0px"
});

const MetricCardHeader = styled("div", {
  label: "AgenticMetricCard-header"
})({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: ".75rem"
});

const MetricCardTitleBlock = styled("div", {
  label: "AgenticMetricCard-titleBlock"
})({
  minWidth: "0px"
});

const MetricCardTitle = styled("div", {
  label: "AgenticMetricCard-title"
})({
  fontSize: "10.5px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".1em",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

/* `&&` doubles the class so this override beats Skeleton's own height
 * utility deterministically — the same result tailwind-merge produced when
 * these were both utility classes. */
const MetricCardValueSkeleton = styled(Skeleton, {
  label: "AgenticMetricCard-valueSkeleton"
})({
  "&&": {
    marginTop: ".75rem",
    height: "1.5rem",
    width: "6rem"
  }
});

const MetricCardError = styled("div", {
  label: "AgenticMetricCard-error"
})({
  marginTop: ".75rem",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  fontWeight: 600,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
});

const MetricCardValue = styled("div", {
  label: "AgenticMetricCard-value",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { compact: boolean } }>(({ ownerState }) => [
  {
    marginTop: ".75rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontWeight: 800,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.compact
    ? { fontSize: "1.25rem", lineHeight: "1.75rem" }
    : { fontSize: "1.5rem", lineHeight: "2rem" }
]);

const MetricCardIconBubble = styled("div", {
  label: "AgenticMetricCard-iconBubble"
})({
  display: "flex",
  width: "2.25rem",
  height: "2.25rem",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-lg)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

const MetricCardFooter = styled("div", {
  label: "AgenticMetricCard-footer"
})({
  marginTop: ".75rem",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: ".75rem"
});

const MetricCardFooterText = styled("div", {
  label: "AgenticMetricCard-footerText"
})({
  minWidth: "0px"
});

const MetricCardSubtitle = styled("div", {
  label: "AgenticMetricCard-subtitle"
})({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: ".75rem",
  lineHeight: "1rem",
  fontWeight: 500,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
});

const MetricCardDescription = styled("div", {
  label: "AgenticMetricCard-description"
})({
  marginTop: ".25rem",
  fontSize: ".75rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const MetricCardTrend = styled("span", {
  label: "AgenticMetricCard-trend",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { trendDirection: TrendDirection } }>(({ ownerState }) => [
  {
    display: "inline-flex",
    flexShrink: 0,
    alignItems: "center",
    gap: ".25rem",
    borderRadius: "var(--bfrs-radius-sm)",
    paddingLeft: ".5rem",
    paddingRight: ".5rem",
    paddingTop: ".25rem",
    paddingBottom: ".25rem",
    fontSize: "11px",
    fontWeight: 700
  },
  trendToneStyles[ownerState.trendDirection]
]);

export const MetricCard = forwardRef<HTMLDivElement, MetricCardProps>(
  ({ className, title, value, subtitle, description, trendValue, trendDirection = "neutral", icon, action, loading, error, compact, ...props }, ref) => (
    <MetricCardRoot ref={ref} padding={compact ? "sm" : "md"} className={className} {...props}>
      <MetricCardHeader>
        <MetricCardTitleBlock>
          <MetricCardTitle>{title}</MetricCardTitle>
          {loading ? (
            <MetricCardValueSkeleton variant="text" />
          ) : error ? (
            <MetricCardError>{error}</MetricCardError>
          ) : (
            <MetricCardValue ownerState={{ compact: Boolean(compact) }}>{value}</MetricCardValue>
          )}
        </MetricCardTitleBlock>
        {icon && <MetricCardIconBubble>{icon}</MetricCardIconBubble>}
      </MetricCardHeader>
      {(subtitle || description || trendValue || action) && (
        <MetricCardFooter>
          <MetricCardFooterText>
            {subtitle && <MetricCardSubtitle>{subtitle}</MetricCardSubtitle>}
            {description && <MetricCardDescription>{description}</MetricCardDescription>}
          </MetricCardFooterText>
          {trendValue && (
            <MetricCardTrend ownerState={{ trendDirection }}>
              {trendIcon[trendDirection]}
              {trendValue}
            </MetricCardTrend>
          )}
          {action}
        </MetricCardFooter>
      )}
    </MetricCardRoot>
  )
);

MetricCard.displayName = "MetricCard";
